import { useEffect, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Settings as SettingsIcon, Sliders, ShieldCheck, FileText, Save, Users, Package, Truck, ShoppingCart, MapPin, Calculator, FileText as FileText2, ClipboardList, CheckSquare, Globe, Lock, User as UserIcon, KeyRound, Smartphone, Crown, Trash2, LogOut, DollarSign, Coins, BarChart3, Lightbulb, FileArchive } from "lucide-react";
import { toast } from "sonner";
import { setActiveCurrency } from "@/lib/money";
import BackupRestore from "./BackupRestore";

const getDeviceId = () => localStorage.getItem("device_id") || "";

const MODULOS = [
  { key: "analytics", label: "Analítica de Ventas", description: "Productos estrella, clientes top, cantones y evolución mensual", Icon: BarChart3 },
  { key: "insights", label: "Insights del negocio", description: "Top clientes del mes, productos sin movimiento, ranking de vendedores y pronóstico", Icon: Lightbulb },
  { key: "customers", label: "Clientes", description: "Gestión de clientela e importación masiva", Icon: Users },
  { key: "costing", label: "Costeo", description: "Costeo integral por producto con margen", Icon: Calculator },
  { key: "documents", label: "Documentos", description: "Archivos del negocio con categorías", Icon: FileText2 },
  { key: "purchase_orders", label: "Órdenes de Compra", description: "Generación de OC a proveedores con PDF en español e inglés", Icon: ClipboardList },
  { key: "orders", label: "Pedidos", description: "Toma de pedidos manuales y extracción por IA", Icon: ShoppingCart },
  { key: "products", label: "Productos", description: "Catálogo de productos, variantes y costos", Icon: Package },
  { key: "suppliers", label: "Proveedores", description: "Directorio de proveedores y categorías", Icon: Truck },
  { key: "routes", label: "Rutas", description: "Planificación de rutas y generación de PDFs", Icon: MapPin },
  { key: "tasks", label: "Tareas", description: "To-do con urgencia, fechas y recordatorios estilo Outlook", Icon: CheckSquare },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modulos, setModulos] = useState({});
  const [company, setCompany] = useState(null);
  const [savingCatalog, setSavingCatalog] = useState(false);

  // Account state
  const [me, setMe] = useState(null);
  const [pinDevices, setPinDevices] = useState([]);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    fetchPreferences();
    fetchCompany();
    fetchMe();
    fetchPinDevices();
  }, []);

  const fetchPreferences = async () => {
    try {
      const res = await api.get("/preferences");
      setModulos(res.data.modulos || {});
    } catch {
      toast.error("Error al cargar preferencias");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompany = async () => {
    try {
      const res = await api.get("/company");
      setCompany(res.data);
    } catch (err) {
      // Non-fatal: /company returns defaults; log for debugging.
      console.warn("[Settings] /company fetch failed:", err?.message || err);
    }
  };

  const fetchMe = async () => {
    try {
      const res = await api.get("/auth/me");
      setMe(res.data);
    } catch (err) {
      // Non-fatal here (token is already validated by PrivateRoute) but log.
      console.warn("[Settings] /auth/me fetch failed:", err?.message || err);
    }
  };

  const fetchPinDevices = async () => {
    try {
      const res = await api.get("/auth/pin/devices");
      setPinDevices(res.data || []);
    } catch {
      setPinDevices([]);
    }
  };

  const currentDeviceId = getDeviceId();
  const currentDeviceHasPin = pinDevices.some((d) => d.device_id === currentDeviceId);

  const openPinDialog = () => {
    setPinValue("");
    setPinConfirm("");
    setPinDialogOpen(true);
  };

  const submitPin = async () => {
    if (!/^\d{4}$/.test(pinValue)) {
      toast.error("El PIN debe ser de 4 dígitos");
      return;
    }
    if (pinValue !== pinConfirm) {
      toast.error("Los PIN no coinciden");
      return;
    }
    setSavingPin(true);
    try {
      const deviceId = currentDeviceId || (window.crypto?.randomUUID?.() || `dev-${Date.now()}`);
      if (!localStorage.getItem("device_id")) localStorage.setItem("device_id", deviceId);
      const label = `${navigator.platform || "Dispositivo"} • ${new Date().toLocaleDateString("es-CR")}`;
      await api.post("/auth/pin/set", { pin: pinValue, device_id: deviceId, device_label: label });
      // remember email for PIN login flow
      const u = me;
      if (u?.email) localStorage.setItem("pin_email", u.email);
      toast.success("PIN configurado para este dispositivo");
      setPinDialogOpen(false);
      fetchPinDevices();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar el PIN");
    } finally {
      setSavingPin(false);
    }
  };

  const removePinForDevice = async (deviceId) => {
    try {
      await api.delete(`/auth/pin?device_id=${encodeURIComponent(deviceId)}`);
      if (deviceId === currentDeviceId) localStorage.removeItem("pin_email");
      toast.success("PIN eliminado de ese dispositivo");
      fetchPinDevices();
    } catch {
      toast.error("Error al eliminar el PIN");
    }
  };

  const removeAllPins = async () => {
    try {
      await api.delete("/auth/pin");
      localStorage.removeItem("pin_email");
      toast.success("PIN eliminado de todos los dispositivos");
      fetchPinDevices();
    } catch {
      toast.error("Error al eliminar los PINs");
    }
  };

  const submitPasswordChange = async () => {
    if (!currentPwd || !newPwd) {
      toast.error("Completá todos los campos");
      return;
    }
    if (newPwd.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (newPwd !== newPwd2) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPwd, new_password: newPwd });
      toast.success("Contraseña actualizada");
      setPwdDialogOpen(false);
      setCurrentPwd("");
      setNewPwd("");
      setNewPwd2("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar la contraseña");
    } finally {
      setSavingPwd(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // We keep pin_email + device_id so PIN login still works on next visit.
    window.location.href = "/login";
  };

  const toggleCatalogoPublico = async () => {
    if (!company) return;
    setSavingCatalog(true);
    try {
      const newValue = !(company.catalogo_publico !== false);
      const next = { ...company, catalogo_publico: newValue };
      await api.put("/company", next);
      setCompany(next);
      toast.success(
        newValue
          ? "Catálogo de imágenes ahora es público"
          : "Catálogo de imágenes ahora es privado"
      );
    } catch {
      toast.error("Error al actualizar la configuración");
    } finally {
      setSavingCatalog(false);
    }
  };

  const [savingCurrency, setSavingCurrency] = useState(false);
  const updateCurrency = async (newMoneda) => {
    if (!company || savingCurrency) return;
    const current = (company.moneda || "CRC").toUpperCase();
    if (current === newMoneda) return;
    setSavingCurrency(true);
    try {
      const next = { ...company, moneda: newMoneda };
      await api.put("/company", next);
      setCompany(next);
      setActiveCurrency(newMoneda);
      toast.success(
        newMoneda === "CRC"
          ? "Moneda cambiada a colones (CRC ₡)"
          : "Moneda cambiada a dólares (USD $)"
      );
    } catch {
      toast.error("Error al actualizar la moneda");
    } finally {
      setSavingCurrency(false);
    }
  };

  const toggleModulo = (key) => {
    setModulos(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await api.put("/preferences", { modulos });
      toast.success("Preferencias guardadas");
      // Broadcast so Layout reloads preferences immediately
      window.dispatchEvent(new Event("preferences-updated"));
    } catch {
      toast.error("Error al guardar preferencias");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Cargando...</div>;

  const activeCount = Object.values(modulos).filter(Boolean).length;

  return (
    <div data-testid="settings-page">
      <div className="mb-6 flex items-center gap-3">
        <SettingsIcon className="h-7 w-7 text-[rgb(0,47,167)]" />
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Ajustes
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">Preferencias de usuario, política de privacidad y términos de uso</p>
        </div>
      </div>

      <Tabs defaultValue="cuenta" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-sm p-1 mb-4" data-testid="settings-tabs">
          <TabsTrigger value="cuenta" data-testid="tab-account" className="rounded-sm data-[state=active]:bg-[rgb(0,47,167)] data-[state=active]:text-white">
            <UserIcon className="h-4 w-4 mr-2" />
            Mi Cuenta
          </TabsTrigger>
          <TabsTrigger value="preferencias" data-testid="tab-preferences" className="rounded-sm data-[state=active]:bg-[rgb(0,47,167)] data-[state=active]:text-white">
            <Sliders className="h-4 w-4 mr-2" />
            Preferencias
          </TabsTrigger>
          <TabsTrigger value="respaldo" data-testid="tab-backup" className="rounded-sm data-[state=active]:bg-[rgb(0,47,167)] data-[state=active]:text-white">
            <FileArchive className="h-4 w-4 mr-2" />
            Respaldo
          </TabsTrigger>
          <TabsTrigger value="privacidad" data-testid="tab-privacy" className="rounded-sm data-[state=active]:bg-[rgb(0,47,167)] data-[state=active]:text-white">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Política de Privacidad
          </TabsTrigger>
          <TabsTrigger value="terminos" data-testid="tab-terms" className="rounded-sm data-[state=active]:bg-[rgb(0,47,167)] data-[state=active]:text-white">
            <FileText className="h-4 w-4 mr-2" />
            Términos de Uso
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cuenta">
          {/* Plan */}
          <Card className="bg-white border border-slate-200 rounded-sm p-6" data-testid="account-plan-section">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-sm bg-amber-50 text-amber-700">
                  <Crown className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    Plan actual
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold bg-amber-50 text-amber-700 border border-amber-200" data-testid="account-plan-badge">
                      Beta — Acceso Completo
                    </span>
                  </h3>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed max-w-xl">
                    Estás usando MiNegocio durante la fase Beta con todos los módulos habilitados sin costo.
                    Cuando lancemos los planes pagos vas a poder ver acá tu plan, fecha de renovación y forma de pago.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Datos de la cuenta */}
          <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-4" data-testid="account-info-section">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-sm bg-blue-50 text-[rgb(0,47,167)]">
                <UserIcon className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Datos de inicio de sesión
                </h3>
                <p className="text-xs text-slate-500 mt-1">Tu identidad en MiNegocio.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">Nombre</div>
                <div className="text-sm font-medium text-slate-900" data-testid="account-name-display">{me?.name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Email</div>
                <div className="text-sm font-medium text-slate-900 break-all" data-testid="account-email-display">{me?.email || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Cuenta creada</div>
                <div className="text-sm font-medium text-slate-900">
                  {me?.created_at ? new Date(me.created_at).toLocaleDateString("es-CR") : "—"}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-slate-200">
              <Button
                onClick={() => setPwdDialogOpen(true)}
                variant="outline"
                className="rounded-sm"
                data-testid="open-change-password-button"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                Cambiar contraseña
              </Button>
              <Button
                onClick={logout}
                variant="outline"
                className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar sesión
              </Button>
            </div>
          </Card>

          {/* PIN management */}
          <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-4" data-testid="account-pin-section">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-sm bg-emerald-50 text-emerald-700">
                  <KeyRound className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    PIN de acceso rápido
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 max-w-xl">
                    Configurá un PIN de 4 dígitos para este dispositivo. En tus próximas visitas vas a entrar
                    con el PIN en vez de escribir tu correo y contraseña. Después de 5 intentos fallidos, el
                    PIN se bloquea 15 minutos por seguridad.
                  </p>
                </div>
              </div>
              <Button
                onClick={openPinDialog}
                className="btn-primary rounded-sm whitespace-nowrap"
                data-testid="open-pin-dialog-button"
              >
                {currentDeviceHasPin ? "Cambiar PIN" : "Configurar PIN"}
              </Button>
            </div>

            {pinDevices.length > 0 ? (
              <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="pin-devices-list">
                {pinDevices.map((d) => (
                  <div key={d.device_id} className="flex items-center justify-between p-3 gap-3" data-testid={`pin-device-${d.device_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <Smartphone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate flex items-center gap-2">
                          {d.device_label || "Dispositivo sin nombre"}
                          {d.device_id === currentDeviceId && (
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                              Este dispositivo
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          Configurado: {d.created_at ? new Date(d.created_at).toLocaleDateString("es-CR") : "—"}
                          {d.last_used_at && ` • Último uso: ${new Date(d.last_used_at).toLocaleDateString("es-CR")}`}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => removePinForDevice(d.device_id)}
                      variant="ghost"
                      size="sm"
                      className="h-8 rounded-sm text-red-600 hover:bg-red-50"
                      data-testid={`remove-pin-${d.device_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Quitar
                    </Button>
                  </div>
                ))}
                <div className="p-2 bg-slate-50 flex justify-end">
                  <Button
                    onClick={removeAllPins}
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs rounded-sm text-red-600 hover:bg-red-50"
                    data-testid="remove-all-pins-button"
                  >
                    Quitar PIN de todos los dispositivos
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="pin-empty-state">
                Todavía no configuraste un PIN. Aplicalo en cualquier dispositivo donde quieras entrar más rápido.
              </p>
            )}
          </Card>

          {/* Moneda */}
          <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-4" data-testid="account-currency-section">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-sm bg-amber-50 text-amber-700">
                <DollarSign className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-base font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Moneda de la empresa
                </h3>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  Elegí en qué moneda querés ver y registrar tus precios y costos.
                  Se aplica al instante en productos, costeo, pedidos, órdenes de compra,
                  catálogos exportados y reportes.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { code: "CRC", title: "Colones costarricenses", symbol: "₡", subtitle: "CRC • formato 1.000", Icon: Coins, color: "emerald" },
                { code: "USD", title: "Dólares estadounidenses", symbol: "$", subtitle: "USD • formato 1,000.00", Icon: DollarSign, color: "blue" },
              ].map(({ code, title, symbol, subtitle, Icon, color }) => {
                const active = (company?.moneda || "CRC").toUpperCase() === code;
                return (
                  <button
                    key={code}
                    type="button"
                    onClick={() => updateCurrency(code)}
                    disabled={savingCurrency}
                    data-testid={`currency-option-${code}`}
                    className={`text-left p-4 rounded-sm border transition-all ${
                      active
                        ? "border-[rgb(0,47,167)] bg-blue-50/40 ring-1 ring-[rgb(0,47,167)]"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    } ${savingCurrency ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`inline-flex items-center justify-center h-9 w-9 rounded-sm font-bold text-lg ${
                        color === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-[rgb(0,47,167)]"
                      }`}>
                        {symbol}
                      </div>
                      {active && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold bg-[rgb(0,47,167)] text-white" data-testid={`currency-active-${code}`}>
                          Activo
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-slate-400" />
                      {title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
                  </button>
                );
              })}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="preferencias">
          <Card className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Módulos del sistema
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Apagá los módulos que tu empresa no utiliza para mantener el menú limpio.
                  Dashboard, Mi Empresa y Ajustes siempre permanecen visibles.
                </p>
                <p className="text-xs text-emerald-700 mt-2 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5 inline-block" data-testid="data-preserved-notice">
                  Apagar un módulo solo lo oculta del menú. Tus datos se conservan y vuelven a aparecer cuando lo encendés de nuevo.
                </p>
              </div>
              <span className="text-xs font-medium px-2.5 py-1 bg-blue-50 text-[rgb(0,47,167)] rounded-sm whitespace-nowrap" data-testid="active-count">
                {activeCount} de {MODULOS.length} activos
              </span>
            </div>

            <div className="space-y-2">
              {MODULOS.map(({ key, label, description, Icon }) => {
                const active = !!modulos[key];
                return (
                  <div
                    key={key}
                    className={`flex items-center justify-between gap-4 p-4 rounded-sm border transition-colors ${
                      active ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50"
                    }`}
                    data-testid={`modulo-row-${key}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`p-2 rounded-sm ${active ? "bg-blue-50 text-[rgb(0,47,167)]" : "bg-slate-100 text-slate-400"}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className={`font-medium ${active ? "text-slate-900" : "text-slate-500"}`}>{label}</div>
                        <div className="text-xs text-slate-500 truncate">{description}</div>
                      </div>
                    </div>
                    <Switch
                      checked={active}
                      onCheckedChange={() => toggleModulo(key)}
                      data-testid={`toggle-${key}`}
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t border-slate-200">
              <Button
                onClick={savePreferences}
                disabled={saving}
                className="btn-primary rounded-sm"
                data-testid="save-preferences-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Guardando..." : "Guardar preferencias"}
              </Button>
            </div>
          </Card>

          {/* Catálogo público */}
          <Card className="bg-white border border-slate-200 rounded-sm p-6 mt-4" data-testid="catalog-privacy-section">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`p-2 rounded-sm ${company?.catalogo_publico !== false ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {company?.catalogo_publico !== false
                    ? <Globe className="h-4 w-4" />
                    : <Lock className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    Privacidad del catálogo de imágenes
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-bold ${
                      company?.catalogo_publico !== false
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-100 text-slate-700 border border-slate-300"
                    }`}>
                      {company?.catalogo_publico !== false ? "Público" : "Privado"}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                    {company?.catalogo_publico !== false
                      ? "Las imágenes de tus productos son accesibles sin login. Es lo que necesita el catálogo de WhatsApp Business para mostrarlas a tus clientes."
                      : "Las imágenes solo se pueden ver con sesión iniciada. El catálogo de WhatsApp NO podrá mostrar las imágenes hasta que vuelvas a hacerlo público."}
                  </p>
                </div>
              </div>
              <Switch
                checked={company?.catalogo_publico !== false}
                onCheckedChange={toggleCatalogoPublico}
                disabled={savingCatalog || !company}
                data-testid="toggle-catalogo-publico"
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="respaldo">
          <BackupRestore />
        </TabsContent>

        <TabsContent value="privacidad">
          <Card className="bg-white border border-slate-200 rounded-sm p-8" data-testid="privacy-policy">
            <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Política de Privacidad
            </h3>
            <p className="text-xs text-slate-500 mb-6">Última actualización: febrero 2026</p>

            <div className="prose prose-sm max-w-none space-y-4 text-slate-700 text-sm leading-relaxed">
              <section>
                <h4 className="font-semibold text-slate-900 mb-1">1. Información que recopilamos</h4>
                <p>
                  MiNegocio recopila únicamente la información que vos ingresás de forma voluntaria al utilizar la
                  aplicación: datos de tu empresa, clientes, proveedores, productos, pedidos, rutas, costeos, colaboradores
                  y archivos subidos a la plataforma. Adicionalmente, almacenamos tu correo electrónico y nombre para la
                  autenticación.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">2. Uso de la información</h4>
                <p>
                  Los datos se utilizan exclusivamente para prestar las funcionalidades del sistema (gestión comercial,
                  costeo, rutas, control de RR.HH., etc.). No vendemos ni compartimos tu información con terceros
                  con fines comerciales.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">3. Inteligencia Artificial</h4>
                <p>
                  La extracción automática de pedidos a partir de imágenes utiliza modelos de IA (GPT-5.2 vía Emergent).
                  Las imágenes enviadas se procesan únicamente para extraer la información del pedido y no se conservan
                  permanentemente fuera de los registros de tu cuenta.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">4. Almacenamiento de archivos</h4>
                <p>
                  Los documentos del negocio y los documentos personales de los colaboradores se almacenan en el servicio
                  de almacenamiento de objetos de Emergent, asociados únicamente a tu usuario y aislados de los datos de
                  otras empresas.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">5. Seguridad</h4>
                <p>
                  Las contraseñas se almacenan con hashing seguro. La autenticación se gestiona vía tokens JWT.
                  Realizamos esfuerzos razonables para proteger tu información, aunque ningún sistema es 100% infalible.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">6. Tus derechos</h4>
                <p>
                  Podés solicitar en cualquier momento la exportación o eliminación de tu cuenta y todos los datos
                  asociados escribiendo a través del soporte del producto.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">7. Cambios a esta política</h4>
                <p>
                  Nos reservamos el derecho de actualizar esta política. Notificaremos los cambios significativos dentro
                  de la propia aplicación.
                </p>
              </section>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="terminos">
          <Card className="bg-white border border-slate-200 rounded-sm p-8" data-testid="terms-of-use">
            <h3 className="text-xl font-bold mb-1" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Términos de Uso
            </h3>
            <p className="text-xs text-slate-500 mb-6">Última actualización: febrero 2026</p>

            <div className="prose prose-sm max-w-none space-y-4 text-slate-700 text-sm leading-relaxed">
              <section>
                <h4 className="font-semibold text-slate-900 mb-1">1. Aceptación de los términos</h4>
                <p>
                  Al crear una cuenta y utilizar MiNegocio aceptás estos términos. Si no estás de acuerdo con alguno
                  de ellos, no debés utilizar la aplicación.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">2. Uso aceptable</h4>
                <p>
                  Te comprometés a utilizar la plataforma de forma lícita, sin subir contenido ilegal, ofensivo o que
                  vulnere derechos de terceros, y a no intentar acceder a datos de otras cuentas ni vulnerar los
                  mecanismos de seguridad.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">3. Responsabilidad de tus datos</h4>
                <p>
                  Sos el único responsable por la veracidad y legalidad de la información que cargás (clientes,
                  pedidos, costos, datos de colaboradores, etc.). Asegurate de cumplir con la normativa de protección
                  de datos personales aplicable en tu país.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">4. Disponibilidad del servicio</h4>
                <p>
                  Hacemos esfuerzos razonables por mantener la plataforma disponible, pero no garantizamos disponibilidad
                  ininterrumpida ni libre de errores. Podemos realizar mantenimientos programados o cambios que afecten
                  temporalmente el servicio.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">5. Propiedad intelectual</h4>
                <p>
                  El software, la marca y la presentación gráfica de MiNegocio son propiedad de sus autores.
                  Los datos comerciales que cargás siguen siendo tuyos.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">6. Limitación de responsabilidad</h4>
                <p>
                  La aplicación se ofrece "tal cual" y no nos hacemos responsables por pérdidas indirectas derivadas
                  del uso o de la imposibilidad de uso del servicio, incluyendo errores de IA al extraer información
                  desde imágenes (siempre revisá los pedidos extraídos antes de registrarlos).
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">7. Cancelación</h4>
                <p>
                  Podés dejar de usar la aplicación en cualquier momento. Nos reservamos el derecho a suspender cuentas
                  que infrinjan estos términos.
                </p>
              </section>

              <section>
                <h4 className="font-semibold text-slate-900 mb-1">8. Cambios a los términos</h4>
                <p>
                  Podemos actualizar estos términos. Las versiones actualizadas se publicarán en esta misma sección con
                  la fecha de última actualización.
                </p>
              </section>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PIN dialog */}
      <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="pin-setup-dialog">
          <DialogHeader>
            <DialogTitle>{currentDeviceHasPin ? "Cambiar PIN" : "Configurar PIN"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Elegí un PIN de 4 dígitos. Vas a usarlo para entrar más rápido en este dispositivo.
            </p>
            <div>
              <label className="block text-xs font-medium mb-1.5">Nuevo PIN</label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinValue}
                onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="rounded-sm tracking-widest text-center text-xl"
                data-testid="pin-input-new"
                placeholder="••••"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Confirmar PIN</label>
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className="rounded-sm tracking-widest text-center text-xl"
                data-testid="pin-input-confirm"
                placeholder="••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPinDialogOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitPin} disabled={savingPin} className="btn-primary rounded-sm" data-testid="pin-save-button">
              {savingPin ? "Guardando..." : "Guardar PIN"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={pwdDialogOpen} onOpenChange={setPwdDialogOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1.5">Contraseña actual</label>
              <Input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                className="rounded-sm"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Nueva contraseña</label>
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                className="rounded-sm"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5">Confirmar nueva contraseña</label>
              <Input
                type="password"
                value={newPwd2}
                onChange={(e) => setNewPwd2(e.target.value)}
                className="rounded-sm"
                data-testid="new-password-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdDialogOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submitPasswordChange} disabled={savingPwd} className="btn-primary rounded-sm" data-testid="change-password-save-button">
              {savingPwd ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
